import { useEffect, useRef, useState } from 'react';
import { Alert, Button } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (options: { formats: string[] }) => BarcodeDetectorLike;

const Detector = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;

/** ¿Este navegador puede leer códigos con la cámara? (Chrome en Android, sí; iPhone todavía no.) */
export const cameraScanSupported = typeof Detector === 'function' && !!navigator.mediaDevices?.getUserMedia;

/**
 * Lee códigos de barras con la cámara trasera. Cada código leído se informa una vez
 * (el mismo código no se repite hasta pasado un segundo y medio).
 */
export function CameraScanner({ open, onClose, onCode }: { open: boolean; onClose: () => void; onCode: (code: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !Detector) return;
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const seen = new Map<string, number>();
    const detector = new Detector({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'] });

    const tick = async () => {
      if (stopped || !video.current) return;
      try {
        const codes = await detector.detect(video.current);
        const now = Date.now();
        for (const c of codes) {
          if ((seen.get(c.rawValue) ?? 0) > now - 1500) continue;
          seen.set(c.rawValue, now);
          setLast(c.rawValue);
          navigator.vibrate?.(60);
          onCode(c.rawValue);
        }
      } catch {
        // cuadro sin imagen todavía: se reintenta
      }
      timer = setTimeout(tick, 250);
    };

    setError(null);
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((s) => {
        stream = s;
        if (stopped || !video.current) return s.getTracks().forEach((t) => t.stop());
        video.current.srcObject = s;
        void video.current.play();
        void tick();
      })
      .catch(() => setError('No se pudo usar la cámara. Revisá que la app tenga permiso para usarla.'));

    return () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [open, onCode]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Escanear con la cámara"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Listo
        </Button>
      }
    >
      <div className="space-y-3">
        {error ? (
          <Alert tone="error">{error}</Alert>
        ) : (
          <video ref={video} muted playsInline className="aspect-video w-full rounded-lg bg-black object-cover" />
        )}
        <p className="text-sm text-slate-500">
          {last ? `Último código: ${last}` : 'Apuntá al código de barras. Cada producto leído se agrega a la venta.'}
        </p>
      </div>
    </Dialog>
  );
}
